import React, { useCallback, useEffect, useRef, useState } from "react";
import OrbitalLoader from "../common/OrbitalLoader";
import ScriptOutputHeader from "./ScriptOutputHeader";
import ScriptDetailsAccordion from "./ScriptDetailsAccordion";
import SceneList from "./SceneList";
import ScriptVideosPanel from "./ScriptVideosPanel";
import { brandsService } from "../../services/brands";

function generateSceneVisualPromise(scriptHash, sceneNumber, options = {}) {
  return new Promise((resolve, reject) => {
    brandsService.generateSceneVisual(scriptHash, sceneNumber, options, resolve, reject);
  });
}

function sceneNeedsVisual(scene) {
  return !scene?.image_hash && !scene?.image_url;
}

/**
 * Shared script output layout: header, collapsible details, scene list.
 * Used by ScriptDetailModal and AdaptScript.
 */
const ScriptOutputView = ({
  script,
  loading,
  error,
  onClose,
  showClose = false,
  onEdit,
  onEditScene,
  onCopyScene,
  onOpenVideos,
  onOpenScript,
  showVideos = false,
  showCreateOnMount = false,
  onVideoCreated,
  onClearCreateParam,
  videoCount = 0,
}) => {
  const sceneListRef = useRef(null);
  const cancelRef = useRef(false);
  const [displayScript, setDisplayScript] = useState(script);
  const [visualStatuses, setVisualStatuses] = useState({});

  useEffect(() => {
    setDisplayScript(script);
    setVisualStatuses({});
    cancelRef.current = false;
  }, [script]);

  useEffect(() => {
    sceneListRef.current?.scrollTo?.(0, 0);
  }, [displayScript?.hash]);

  const runGeneration = useCallback(async (scenesToGenerate, options = {}) => {
    if (!displayScript?.hash || scenesToGenerate.length === 0) return;

    for (const scene of scenesToGenerate) {
      if (cancelRef.current) return;

      const sceneNumber = scene.scene_number;
      setVisualStatuses((prev) => ({ ...prev, [sceneNumber]: "generating" }));

      try {
        const result = await generateSceneVisualPromise(displayScript.hash, sceneNumber, options);
        if (cancelRef.current) return;

        setDisplayScript((prev) => {
          const scenes = (prev?.script_data?.scenes || []).map((item) =>
            item.scene_number === sceneNumber
              ? {
                  ...item,
                  ...result.scene,
                  image_hash: result.asset_hash,
                  image_url: result.image_url,
                }
              : item
          );
          return {
            ...prev,
            script_data: {
              ...(prev?.script_data || {}),
              scenes,
            },
          };
        });
        setVisualStatuses((prev) => {
          const next = { ...prev };
          delete next[sceneNumber];
          return next;
        });
      } catch {
        if (cancelRef.current) return;
        setVisualStatuses((prev) => ({ ...prev, [sceneNumber]: "failed" }));
        return;
      }
    }
  }, [displayScript?.hash]);

  useEffect(() => {
    if (!displayScript?.hash || showVideos) return undefined;

    const scenes = Array.isArray(displayScript?.script_data?.scenes)
      ? displayScript.script_data.scenes
      : [];
    const queue = scenes.filter(sceneNeedsVisual);
    if (queue.length === 0) return undefined;

    cancelRef.current = false;
    runGeneration(queue);

    return () => {
      cancelRef.current = true;
    };
  }, [displayScript?.hash, runGeneration, showVideos]);

  const handleRetryVisual = useCallback(
    (sceneNumber) => {
      const scenes = displayScript?.script_data?.scenes || [];
      const scene = scenes.find((item) => item.scene_number === sceneNumber);
      if (!scene) return;
      runGeneration([scene]);
    },
    [displayScript?.script_data?.scenes, runGeneration]
  );

  const handleRegenerateVisual = useCallback(
    async (sceneNumber, prompt) => {
      const scenes = displayScript?.script_data?.scenes || [];
      const scene = scenes.find((item) => item.scene_number === sceneNumber);
      if (!scene) return;
      await runGeneration([scene], { regenerate: true, prompt });
    },
    [displayScript?.script_data?.scenes, runGeneration]
  );

  if (loading) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="text-center py-10 text-theme-text">
          <OrbitalLoader size={64} text="Loading script..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
          {error}
        </div>
      </div>
    );
  }

  if (!displayScript) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
          Script not found.
        </div>
      </div>
    );
  }

  const scenes = Array.isArray(displayScript?.script_data?.scenes)
    ? displayScript.script_data.scenes
    : [];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ScriptOutputHeader
        script={displayScript}
        showClose={showClose}
        onClose={onClose}
        onEdit={onEdit}
        onOpenVideos={onOpenVideos}
        videosActive={showVideos}
        videoCount={videoCount || displayScript.video_count || 0}
      />
      {showVideos ? (
        <ScriptVideosPanel
          script={displayScript}
          showCreateOnMount={showCreateOnMount}
          onBack={onOpenScript}
          onVideoCreated={onVideoCreated}
          onClearCreateParam={onClearCreateParam}
        />
      ) : (
        <>
          <ScriptDetailsAccordion script={displayScript} />
          <div ref={sceneListRef} className="flex-1 min-h-0 overflow-y-auto pr-1">
            <SceneList
              scenes={scenes}
              visualStatuses={visualStatuses}
              onCopyScene={onCopyScene}
              onEditScene={onEditScene}
              onRetryVisual={handleRetryVisual}
              onRegenerateVisual={handleRegenerateVisual}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ScriptOutputView;
